import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { InterventionTemplateResponse } from '../../api/generated';
import type { RootState } from '../index';

interface InterventionTemplatesState {
  templatesById: Record<string, InterventionTemplateResponse>;
  loading: boolean;
  error: string | null;
}

const initialState: InterventionTemplatesState = {
  templatesById: {},
  loading: false,
  error: null
};

const interventionTemplatesSlice = createSlice({
  name: 'interventionTemplates',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setTemplates: (state, action: PayloadAction<InterventionTemplateResponse[]>) => {
      const templatesById: Record<string, InterventionTemplateResponse> = {};
      for (const iv of action.payload) {
        templatesById[iv.id] = iv;
      }
      state.templatesById = templatesById;
      state.loading = false;
      state.error = null;
    },
    upsertTemplate: (state, action: PayloadAction<InterventionTemplateResponse>) => {
      state.templatesById[action.payload.id] = action.payload;
    },
    removeTemplate: (state, action: PayloadAction<string>) => {
      delete state.templatesById[action.payload];
    },
    clearTemplates: (state) => {
      state.templatesById = {};
      state.loading = false;
      state.error = null;
    }
  }
});

export const {
  setLoading,
  setError,
  setTemplates,
  upsertTemplate,
  removeTemplate,
  clearTemplates
} = interventionTemplatesSlice.actions;

export const selectInterventionTemplates = (state: RootState): InterventionTemplateResponse[] =>
  Object.values(state.interventionTemplatesSlice.templatesById);

export const selectInterventionTemplateById =
  (id: string | undefined) =>
  (state: RootState): InterventionTemplateResponse | undefined =>
    id ? state.interventionTemplatesSlice.templatesById[id] : undefined;

export const selectInterventionTemplatesLoading = (state: RootState): boolean =>
  state.interventionTemplatesSlice.loading;

export const selectInterventionTemplatesError = (state: RootState): string | null =>
  state.interventionTemplatesSlice.error;

export default interventionTemplatesSlice.reducer;
